import { useState, useCallback, useRef } from "react";
import {
  type ServerSideConfig,
  type ServerSideParams,
  type ExportProgress,
  type BatchedExportConfig,
  DEFAULT_BATCHED_EXPORT_CONFIG,
} from "../types";

export function useBatchedExport<T>(
  serverSideConfig: ServerSideConfig<T> | undefined,
  batchConfig: BatchedExportConfig = DEFAULT_BATCHED_EXPORT_CONFIG
) {
  const [progress, setProgress] = useState<ExportProgress>({
    isExporting: false,
    stage: "preparing",
    totalBatches: 0,
    completedBatches: 0,
    currentBatch: 0,
    totalRecords: 0,
    processedRecords: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const startTimeRef = useRef<number>(0);

  const shouldUseBatchedExport = useCallback(
    (totalCount: number): boolean => {
      if (!batchConfig.enabled || !serverSideConfig?.enabled) return false;

      if (batchConfig.shouldUseBatchedExport) {
        return batchConfig.shouldUseBatchedExport(totalCount);
      }

      return totalCount > (batchConfig.threshold || 5000);
    },
    [batchConfig, serverSideConfig]
  );

  const calculateProgress = useCallback(
    (
      completedBatches: number,
      totalBatches: number,
      processedRecords: number,
      totalRecords: number
    ) => {
      const elapsed = Date.now() - startTimeRef.current;
      const progressRatio = processedRecords / totalRecords;

      let estimatedTimeRemaining: number | undefined;
      if (progressRatio > 0.1) {
        const estimatedTotal = elapsed / progressRatio;
        estimatedTimeRemaining = Math.max(0, estimatedTotal - elapsed);
      }

      return {
        completedBatches,
        totalBatches,
        processedRecords,
        totalRecords,
        estimatedTimeRemaining,
      };
    },
    []
  );

  const fetchBatch = useCallback(
    async (
      batchNumber: number,
      batchSize: number,
      filters: any,
      sorting: any,
      dateRange: any
    ): Promise<T[]> => {
      if (!serverSideConfig?.onDataRequest) {
        throw new Error("Server-side configuration not available");
      }

      const params: ServerSideParams = {
        page: batchNumber,
        limit: batchSize,
        ...filters,
        ...(sorting.field &&
          sorting.direction && {
            sortBy: sorting.field,
            sortOrder: sorting.direction,
          }),
        ...(dateRange.field &&
          dateRange.range !== "all" && {
            dateField: dateRange.field,
            dateRange: dateRange.range,
            ...(dateRange.startDate && { startDate: dateRange.startDate }),
            ...(dateRange.endDate && { endDate: dateRange.endDate }),
          }),
      };

      const response = await serverSideConfig.onDataRequest(params, true);

      if (!response.success) {
        throw new Error(`Batch ${batchNumber} failed: ${response.message}`);
      }

      return response.data;
    },
    [serverSideConfig]
  );

  const exportInBatches = useCallback(
    async (
      totalCount: number,
      filters: any,
      sorting: any,
      dateRange: any,
      onBatchComplete?: (batchData: T[], batchNumber: number) => void
    ): Promise<T[]> => {
      const batchSize = batchConfig.batchSize || 2500;
      const totalBatches = Math.ceil(totalCount / batchSize);
      const maxConcurrent = Math.min(
        batchConfig.maxConcurrentBatches || 2,
        totalBatches
      );

      startTimeRef.current = Date.now();
      abortControllerRef.current = new AbortController();

      setProgress({
        isExporting: true,
        stage: "fetching",
        totalBatches,
        totalRecords: totalCount,
        completedBatches: 0,
        currentBatch: 0,
        processedRecords: 0,
      });

      const allData: T[] = [];
      const errors: string[] = [];
      let completedBatches = 0;

      try {
        // Process batches with controlled concurrency
        for (let i = 0; i < totalBatches; i += maxConcurrent) {
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error("Export cancelled by user");
          }

          // Create batch promises for current concurrency window
          const batchPromises = [];
          for (let j = 0; j < maxConcurrent && i + j < totalBatches; j++) {
            const batchNumber = i + j + 1; // 1-based page numbers
            batchPromises.push(
              fetchBatch(batchNumber, batchSize, filters, sorting, dateRange)
                .then(async (batchData) => {
                  // Add delay to reduce server load
                  if (batchConfig.batchDelay && batchConfig.batchDelay > 0) {
                    await new Promise((resolve) =>
                      setTimeout(resolve, batchConfig.batchDelay)
                    );
                  }

                  onBatchComplete?.(batchData, batchNumber);
                  return { batchNumber, data: batchData, error: null };
                })
                .catch((error) => {
                  console.error(`Batch ${batchNumber} failed:`, error);
                  return { batchNumber, data: [], error: error.message };
                })
            );
          }

          // Wait for current batch window to complete
          const batchResults = await Promise.all(batchPromises);

          // Process results
          for (const result of batchResults) {
            if (result.error) {
              errors.push(`Batch ${result.batchNumber}: ${result.error}`);
            } else {
              allData.push(...result.data);
            }

            completedBatches++;
            const processedRecords = allData.length;

            // Update progress
            const progressUpdate = calculateProgress(
              completedBatches,
              totalBatches,
              processedRecords,
              totalCount
            );
            setProgress((prev) => ({
              ...prev,
              currentBatch: result.batchNumber,
              ...progressUpdate,
            }));
          }
        }

        // Handle errors
        if (errors.length > 0) {
          const errorMessage = `Some batches failed: ${errors.join("; ")}`;
          console.warn(errorMessage);

          // If too many errors, throw
          if (errors.length > totalBatches * 0.5) {
            throw new Error(`Export failed: ${errorMessage}`);
          }
        }

        // Set processing stage
        setProgress((prev) => ({
          ...prev,
          stage: "processing",
        }));

        // Mark as complete
        setProgress((prev) => ({
          ...prev,
          stage: "complete",
          isExporting: false,
        }));

        return allData;
      } catch (error) {
        let errorMessage = "Unknown error";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }
        setProgress((prev) => ({
          ...prev,
          stage: "error",
          isExporting: false,
          error: errorMessage,
        }));
        throw error;
      }
    },
    [batchConfig, fetchBatch, calculateProgress]
  );

  const cancelExport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setProgress((prev) => ({
      ...prev,
      isExporting: false,
      stage: "error",
      error: "Export cancelled by user",
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({
      isExporting: false,
      stage: "preparing",
      totalBatches: 0,
      completedBatches: 0,
      currentBatch: 0,
      totalRecords: 0,
      processedRecords: 0,
    });
  }, []);

  return {
    progress,
    shouldUseBatchedExport,
    exportInBatches,
    cancelExport,
    resetProgress,
  };
}
